import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useAuth } from '../../context/AuthContext';
import { createLogger } from '../../utils/logger';
import { supabase } from '../../utils/supabase';
import { formatDate } from '../../utils/dateUtils';
import { IoTimeOutline, IoShieldCheckmarkOutline, IoRefreshOutline } from 'react-icons/io5';
import type { ConsentAuditEntry } from '../../types/privacy';
import { CONSENT_CATEGORIES } from '../../constants/privacy';

const logger = createLogger({ module: 'ConsentHistory' });

interface ConsentHistoryProps {
  className?: string;
  maxEntries?: number;
}

export const ConsentHistory: React.FC<ConsentHistoryProps> = ({
  className = '',
  maxEntries = 10
}) => {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const [history, setHistory] = useState<ConsentAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConsentHistory = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('consent_audit_log')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(maxEntries);

      if (fetchError) {
        throw fetchError;
      }

      setHistory(data || []);
      logger.info('Consent history loaded', { entriesCount: data?.length || 0 });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load consent history';
      setError(errorMessage);
      logger.error('Error loading consent history', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsentHistory();
  }, [user]);

  const formatConsentChanges = (entry: ConsentAuditEntry) => {
    const changes: string[] = [];
    
    // For the current implementation, we only have the current consent preferences
    // We can show what was set for each category
    if (entry.consent_preferences) {
      Object.entries(entry.consent_preferences).forEach(([key, value]) => {
        const categoryName = CONSENT_CATEGORIES[key as keyof typeof CONSENT_CATEGORIES]?.title || key;
        changes.push(`${categoryName}: ${value ? 'Enabled' : 'Disabled'}`);
      });
    }
    
    return changes.length > 0 ? changes : ['No changes recorded'];
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'initial_consent':
        return <IoShieldCheckmarkOutline className="w-4 h-4 text-green-500" />;
      case 'consent_updated':
        return <IoRefreshOutline className="w-4 h-4 text-blue-500" />;
      case 'consent_withdrawn':
        return <IoTimeOutline className="w-4 h-4 text-red-500" />;
      default:
        return <IoTimeOutline className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'initial_consent':
        return 'Initial Consent';
      case 'consent_updated':
        return 'Preferences Updated';
      case 'consent_withdrawn':
        return 'Consent Withdrawn';
      default:
        return 'Unknown Action';
    }
  };

  if (loading) {
    return (
      <div className={`p-4 rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      } ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className={`ml-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading consent history...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg border ${
        darkMode ? 'border-red-700 bg-red-900/20' : 'border-red-200 bg-red-50'
      } ${className}`}>
        <div className="flex items-center">
          <div className="text-red-500">
            <IoTimeOutline className="w-5 h-5" />
          </div>
          <div className="ml-3">
            <h3 className={`font-medium ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
              Error Loading History
            </h3>
            <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
              {error}
            </p>
          </div>
        </div>
        <button
          onClick={fetchConsentHistory}
          className="mt-3 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`p-4 rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      } ${className}`}>
        <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Please log in to view your consent history.
        </p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className={`p-4 rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      } ${className}`}>
        <div className="text-center py-8">
          <IoTimeOutline className={`w-12 h-12 mx-auto mb-3 ${
            darkMode ? 'text-gray-600' : 'text-gray-400'
          }`} />
          <h3 className={`font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            No Consent History
          </h3>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Your consent changes will appear here once you update your preferences.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${
      darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
    } ${className}`}>
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Consent History
          </h3>
          <button
            onClick={fetchConsentHistory}
            className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
            title="Refresh history"
          >
            <IoRefreshOutline className="w-4 h-4" />
          </button>
        </div>
        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Track of all changes to your privacy preferences
        </p>
      </div>

      <div className="p-4">
        <div className="space-y-4">
          {history.map((entry, index) => {
            const changes = formatConsentChanges(entry);
            
            return (
              <div key={entry.id} className={`flex items-start space-x-3 ${
                index !== history.length - 1 ? 'pb-4 border-b border-gray-200 dark:border-gray-700' : ''
              }`}>
                <div className="shrink-0 mt-1">
                  {getActionIcon(entry.action)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {getActionLabel(entry.action)}
                    </h4>
                    <time className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatDate(entry.created_at)}
                    </time>
                  </div>
                  
                  {changes.length > 0 && (
                    <div className="mt-2">
                      <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {changes.map((change, changeIndex) => (
                          <li key={changeIndex} className="flex items-center">
                            <span className="w-1 h-1 bg-gray-400 rounded-full mr-2 shrink-0"></span>
                            {change}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {entry.ip_address && (
                    <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      IP: {entry.ip_address}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {history.length >= maxEntries && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className={`text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Showing last {maxEntries} entries. Contact support for complete history.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}; 